/**
 * Appointment Adapter
 *
 * Transforms Appointment data into UnifiedEncounterItem format
 * for use in the EncounterList component (standalone mode).
 */

import { Appointment } from '@/types/appointment';
import { UnifiedEncounterItem, RecordingStatus, TranscriptionStatus, EncounterStatus } from '../types';

/**
 * Extract patient name from appointment
 * Handles both string IDs and populated patient objects
 */
function getPatientName(patientId: string | Record<string, unknown>): string {
  if (typeof patientId === 'string') {
    return patientId;
  }

  const patient = patientId as { firstName?: string; lastName?: string };
  const firstName = patient.firstName || '';
  const lastName = patient.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();

  return fullName || 'Unknown Patient';
}

/**
 * Extract patient MRN from appointment if available
 */
function getPatientMrn(patientId: string | Record<string, unknown>): string | undefined {
  if (typeof patientId === 'string') {
    return undefined;
  }

  const patient = patientId as { mrn?: string };
  return patient.mrn;
}

/**
 * Extract patient ID string
 */
function getPatientIdString(patientId: string | Record<string, unknown>): string | undefined {
  if (typeof patientId === 'string') {
    return patientId;
  }

  const patient = patientId as { id?: string };
  return patient.id;
}

/**
 * Extract provider name from appointment
 * Handles both string IDs and populated provider objects
 */
function getProviderName(providerId: string | Record<string, unknown>): string | undefined {
  if (typeof providerId === 'string') {
    return undefined;
  }

  const provider = providerId as { firstName?: string; lastName?: string };
  const firstName = provider.firstName || '';
  const lastName = provider.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();

  return fullName || undefined;
}

/**
 * Extract provider ID string
 */
function getProviderIdString(providerId: string | Record<string, unknown>): string | undefined {
  if (typeof providerId === 'string') {
    return providerId;
  }

  const provider = providerId as { id?: string };
  return provider.id;
}

/**
 * Map recording status to overall encounter status
 */
function mapToEncounterStatus(
  recordingStatus?: RecordingStatus,
  transcriptionStatus?: TranscriptionStatus
): EncounterStatus {
  if (recordingStatus === 'failed' || transcriptionStatus === 'failed') {
    return 'failed';
  }
  if (recordingStatus === 'recording') {
    return 'recording';
  }
  if (recordingStatus === 'completed' && transcriptionStatus === 'completed') {
    return 'completed';
  }
  if (recordingStatus === 'completed' || transcriptionStatus === 'processing') {
    return 'in-progress';
  }
  return 'scheduled';
}

/**
 * Transform a single Appointment into UnifiedEncounterItem
 */
export function adaptAppointment(appointment: Appointment): UnifiedEncounterItem {
  // Access recording and transcription status directly (they are required fields in Appointment type)
  const recordingStatus = appointment.recording.status as RecordingStatus;
  const transcriptionStatus = appointment.transcription.status as TranscriptionStatus;

  // Prefer the populated patient object; fall back to the bare id string.
  const patientRef = (appointment.patient ?? appointment.patientId) as string | Record<string, unknown>;

  return {
    id: appointment.id,
    patientName: getPatientName(patientRef),
    patientMrn: getPatientMrn(patientRef),
    patientId: getPatientIdString(patientRef),
    scheduledDate: new Date(appointment.appointmentDetails.scheduledDate),
    type: appointment.appointmentDetails.type,
    location: appointment.appointmentDetails.location,
    chiefComplaint: appointment.appointmentDetails.chiefComplaint,
    providerName: getProviderName((appointment.provider ?? appointment.providerId) as string | Record<string, unknown>),
    providerId: getProviderIdString(appointment.providerId as string | Record<string, unknown>),
    // A cancelled or no-show visit shows as that; otherwise its capture status.
    status: appointment.visit && appointment.visit.status !== 'scheduled'
      ? appointment.visit.status
      : mapToEncounterStatus(recordingStatus, transcriptionStatus),
    recordingStatus,
    transcriptionStatus,
    matchStatus: undefined, // Not applicable in standalone mode
    externalSystem: undefined,
    externalId: undefined,
    hasTranscript: transcriptionStatus === 'completed',
    canStartRecording: recordingStatus === 'pending' && (appointment.visit?.status ?? 'scheduled') === 'scheduled',
    _raw: appointment,
    _type: 'appointment',
  };
}

/**
 * Transform an array of Appointments into UnifiedEncounterItems
 */
export function adaptAppointments(appointments: Appointment[]): UnifiedEncounterItem[] {
  return appointments.map(adaptAppointment);
}

export default adaptAppointment;
